
import { useState, useRef, useEffect } from 'react';
import { BellIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications, TenderNotification } from '@/hooks/useNotifications';
import { NotificationList } from './NotificationList';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { TenderDetails } from '@/components/dashboard/TenderDetails';

export const NotificationCenter = () => {
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead, fetchNotifications } = useNotifications();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTender, setSelectedTender] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside to close the popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Initial fetch of notifications
    fetchNotifications();
    
    // Set up a periodic refresh (every minute)
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  const handleNotificationClick = (notification: TenderNotification) => {
    // Mark as read first
    markAsRead(notification.tender_id);
    
    // Store the selected tender for the TenderDetails component
    setSelectedTender(notification.tender_data);
    setIsDetailsOpen(true);
    
    // Close the popover
    setIsPopoverOpen(false);
  };

  const handleViewAllClick = () => {
    setIsPopoverOpen(false);
    setIsModalOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);
    if (open) {
      // Refresh notifications when opening
      fetchNotifications();
    }
  };

  // Handle saving the tender
  const handleSaveTender = async () => {
    setIsDetailsOpen(false);
  };

  return (
    <>
      <div ref={popoverRef}>
        <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="relative p-2 h-9 w-9"
              aria-label={t('notifications.title')}
            >
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 h-5 w-5 bg-red-500 text-[10px] font-bold rounded-full flex items-center justify-center text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <BellIcon className={cn(
                "h-5 w-5", 
                unreadCount > 0 
                  ? "text-primary animate-pulse" 
                  : "text-muted-foreground"
              )} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0 bg-background border shadow-md" align="end" sideOffset={5}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-medium">{t('notifications.title')}</h3>
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={markAllAsRead}
                >
                  {t('notifications.markAllAsRead')}
                </Button>
              )}
            </div>
            
            <NotificationList 
              notifications={notifications.slice(0, 5)}
              isLoading={isLoading}
              onNotificationClick={handleNotificationClick}
              maxHeight="300px" 
            />
            
            <div className="p-2 border-t border-border">
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={handleViewAllClick}
              >
                {t('notifications.viewAll')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col bg-background">
          <DialogHeader>
            <DialogTitle>{t('notifications.allNotifications')}</DialogTitle>
          </DialogHeader>
          <div className="flex-grow overflow-hidden">
            <NotificationList 
              notifications={notifications}
              isLoading={isLoading}
              onNotificationClick={handleNotificationClick}
              maxHeight="calc(80vh - 120px)"
            />
          </div>
        </DialogContent>
      </Dialog>
      
      <TenderDetails
        tender={selectedTender}
        isOpen={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        onSaveTender={handleSaveTender}
      />
      
      <Toaster />
    </>
  );
};
